import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { LanguageUtils } from '@pbt/pbt-ui-components'

import {
  getDrugAdministrationFrequencies,
  getDrugDeliveryMethod,
  getPrescriptionProductSizeUnit,
  getProductSizeUnit,
} from '~/store/reducers/constants'
import type { Prescription } from '~/types'
import {
  getLengthOfCourseString,
  getPrescriptionQuantityUnits,
} from '~/utils/prescriptionUtils'

import { LabelValueWrapper } from './LabelValueWrapper'

import styles from './AdministrationInstructionsSection.module.scss'

interface AdministrationField {
  label: string
  value: string | null
}

/**
 * Helper hook to extract individual administration instruction fields from prescription
 */
const useAdministrationFields = (
  prescription: Prescription,
): AdministrationField[] => {
  const { t } = useTranslation(['Common', 'Dialogs'])
  const DrugDeliveryMethod = useSelector(getDrugDeliveryMethod)
  const DrugAdministrationFrequencies = useSelector(
    getDrugAdministrationFrequencies,
  )
  const ProductSizeUnit = useSelector(getProductSizeUnit)
  const PrescriptionProductSizeUnit = useSelector(
    getPrescriptionProductSizeUnit,
  )

  const {
    administrationQuantity,
    administrationRouteId,
    administrationFrequencyId,
    administrationFrequencyNotes,
    administrationCourseLength,
    administrationCourseType,
  } = prescription

  const { administrationQuantityUnit } = getPrescriptionQuantityUnits(
    prescription,
    {
      ProductSizeUnit,
      PrescriptionProductSizeUnit,
    },
  )

  const administerValue =
    administrationQuantity != null && administrationQuantityUnit
      ? `${administrationQuantity} ${administrationQuantityUnit}`
      : null

  const routeValue =
    LanguageUtils.getConstantTranslatedName(
      administrationRouteId,
      DrugDeliveryMethod,
      '',
    ) || null

  const frequencyName = LanguageUtils.getConstantTranslatedName(
    administrationFrequencyId,
    DrugAdministrationFrequencies,
    '',
  )
  const frequencyValue =
    frequencyName === t('Common:OTHER')
      ? administrationFrequencyNotes || null
      : frequencyName || null

  const lengthOfCourseValue =
    getLengthOfCourseString({
      administrationCourseType,
      administrationCourseLength,
    }) || null

  return [
    {
      label: t('Common:ADMINISTER_ACTION'),
      value: administerValue,
    },
    {
      label: t('Common:ROUTE_OF_DRUG_ADMINISTRATION'),
      value: routeValue,
    },
    {
      label: t('Common:FREQUENCY_ONE'),
      value: frequencyValue,
    },
    {
      label: t('Dialogs:PRESCRIPTION_DIALOG.PERIOD_OF_TIME'),
      value: lengthOfCourseValue,
    },
  ]
}

export interface AdministrationInstructionsSectionProps {
  prescription: Prescription
}

export const AdministrationInstructionsSection = ({
  prescription,
}: AdministrationInstructionsSectionProps) => {
  const { t } = useTranslation(['Common', 'Dialogs'])
  const administrationFields = useAdministrationFields(prescription)

  return (
    <>
      <div className={styles.sectionHeader}>
        {t('Dialogs:PRESCRIPTION_DIALOG.ADMINISTRATION_INSTRUCTIONS')}
      </div>
      <div className={styles.administrationGrid}>
        {administrationFields.map((field) => (
          <LabelValueWrapper
            key={field.label}
            label={field.label}
            value={field.value ?? ''}
          />
        ))}
      </div>
    </>
  )
}
